import { C } from "@/brand/tokens";
import { process5 } from "@/brand/content";

/**
 * ProcessSection5 — Dry Stone chamber.
 *
 * Five-step engagement process laid out as a disciplined vertical
 * timeline: brass vertical rail on the left, five milestones stacked
 * along it, each with a brass Cormorant ordinal and a short label
 * plus body. Calmer than Concept 4's staggered axis but the brass
 * spine is preserved.
 */
export function ProcessSection5() {
  return (
    <section
      id="process"
      data-testid="process"
      style={{
        background: C.surfaceLight,
        color: C.textOnLight,
        borderTop: `1px solid ${C.paperOver}`,
      }}
    >
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-4 mb-7">
              <span
                aria-hidden="true"
                className="inline-block h-px w-14"
                style={{ background: C.accentMetal }}
              />
              <p
                className="font-inter text-[11px] uppercase font-semibold"
                style={{
                  color: C.accentMetal,
                  letterSpacing: "0.28em",
                }}
              >
                Process
              </p>
            </div>
            <h2
              className="font-cormorant"
              style={{
                color: C.textOnLight,
                fontSize: "clamp(2rem, 3.6vw, 3rem)",
                fontWeight: 400,
                lineHeight: 1.06,
                letterSpacing: "-0.012em",
              }}
            >
              A clear path
              <br />
              from inquiry
              <br />
              to engagement.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 lg:pt-3">
            <p
              className="font-inter leading-[1.7] max-w-[42ch]"
              style={{ color: C.inkOnLight, fontSize: "1rem" }}
            >
              Reaching out shouldn’t feel like a leap. Here is exactly what
              happens between the moment you submit an inquiry and the start
              of the work.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-2" />
          <ol
            className="lg:col-span-10 relative"
            data-testid="process-list"
            style={{ paddingLeft: 0 }}
          >
            {/* Brass vertical rail */}
            <span
              aria-hidden="true"
              className="absolute top-0 bottom-0"
              style={{
                left: "clamp(2.4rem, 4vw, 4rem)",
                width: 1,
                background: C.accentMetal,
              }}
            />
            {process5.map((s, i) => (
              <li
                key={s.n}
                data-testid={`process-step-${i}`}
                className="relative grid grid-cols-12 gap-x-6 py-9"
                style={{
                  borderBottom:
                    i === process5.length - 1
                      ? "none"
                      : `1px solid ${C.paperOver}`,
                }}
              >
                {/* Ordinal — sits on the rail */}
                <div className="col-span-2 lg:col-span-2 flex items-start relative">
                  <span
                    className="font-cormorant tabular-nums px-3"
                    style={{
                      color: C.accentMetal,
                      fontSize: "clamp(1.8rem, 2.4vw, 2.4rem)",
                      fontWeight: 400,
                      lineHeight: 1,
                      background: C.surfaceLight,
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    {s.n}
                  </span>
                </div>

                <div className="col-span-10 lg:col-span-4">
                  <h3
                    className="font-inter text-[12px] uppercase font-semibold"
                    style={{
                      color: C.accentFunctional,
                      letterSpacing: "0.20em",
                      lineHeight: 1.4,
                    }}
                  >
                    {s.label}
                  </h3>
                </div>

                <div className="col-span-12 lg:col-span-6">
                  <p
                    className="font-inter leading-[1.65] max-w-[50ch]"
                    style={{ color: C.inkOnLight, fontSize: "0.98rem" }}
                  >
                    {s.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <p
          className="mt-12 font-inter text-[12.5px] leading-[1.7] max-w-[60ch]"
          style={{ color: C.inkOnLightSoft }}
        >
          Every inquiry is handled by Katrina directly. If Faith 1st is not
          the right fit, we’ll often refer you to someone better suited.
        </p>
      </div>
    </section>
  );
}
